import { useState, useCallback } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { api } from '../api';
import { useOrgInfo } from '../contexts/OrgInfoContext';
import { useBeheerder } from '../hooks/useBeheerder';
import { useTranslation } from '../i18n/useTranslation';
import type { TranslationKey } from '../i18n';

type Tier = 'free' | 'team' | 'enterprise';

const ACCENT = {
  free: { main: '#31679B', light: '#EBF2F8', icon: '#31679B' },
  team: { main: '#0D7377', light: '#E6F5F5', icon: '#0D7377' },
  enterprise: { main: '#8B6914', light: '#FDF8EE', icon: '#8B6914' },
};

interface Feature {
  labelKey: TranslationKey;
  icon: React.ReactNode;
}

interface TierConfig {
  id: Tier;
  labelKey: TranslationKey;
  taglineKey: TranslationKey;
  heroIcon: React.ReactNode;
  features: Feature[];
}

const iconSx = (color: string) => ({ fontSize: 15, color });

const TIERS: TierConfig[] = [
  {
    id: 'free',
    labelKey: 'pricing.tier.free.label',
    taglineKey: 'pricing.tier.free.tagline',
    heroIcon: <RocketLaunchOutlinedIcon sx={{ fontSize: 26, color: ACCENT.free.icon }} />,
    features: [
      { labelKey: 'pricing.tier.free.feature.mcps', icon: <ExtensionOutlinedIcon sx={iconSx(ACCENT.free.main)} /> },
      { labelKey: 'pricing.tier.free.feature.seats', icon: <PersonOutlinedIcon sx={iconSx(ACCENT.free.main)} /> },
      { labelKey: 'pricing.tier.free.feature.calls', icon: <BoltOutlinedIcon sx={iconSx(ACCENT.free.main)} /> },
    ],
  },
  {
    id: 'team',
    labelKey: 'pricing.tier.team.label',
    taglineKey: 'pricing.tier.team.tagline',
    heroIcon: <GroupsOutlinedIcon sx={{ fontSize: 26, color: ACCENT.team.icon }} />,
    features: [
      { labelKey: 'pricing.tier.team.feature.mcps', icon: <ExtensionOutlinedIcon sx={iconSx(ACCENT.team.main)} /> },
      { labelKey: 'pricing.tier.team.feature.seats', icon: <PersonOutlinedIcon sx={iconSx(ACCENT.team.main)} /> },
      { labelKey: 'pricing.tier.team.feature.calls', icon: <BoltOutlinedIcon sx={iconSx(ACCENT.team.main)} /> },
    ],
  },
  {
    id: 'enterprise',
    labelKey: 'pricing.tier.enterprise.label',
    taglineKey: 'pricing.tier.enterprise.tagline',
    heroIcon: <WorkspacePremiumOutlinedIcon sx={{ fontSize: 26, color: ACCENT.enterprise.icon }} />,
    features: [
      { labelKey: 'pricing.tier.enterprise.feature.mcps', icon: <AllInclusiveIcon sx={iconSx(ACCENT.enterprise.main)} /> },
      { labelKey: 'pricing.tier.enterprise.feature.seats', icon: <PersonOutlinedIcon sx={iconSx(ACCENT.enterprise.main)} /> },
      { labelKey: 'pricing.tier.enterprise.feature.calls', icon: <BoltOutlinedIcon sx={iconSx(ACCENT.enterprise.main)} /> },
      { labelKey: 'pricing.tier.enterprise.feature.chat', icon: <CloudOutlinedIcon sx={iconSx(ACCENT.enterprise.main)} /> },
      { labelKey: 'pricing.tier.enterprise.feature.governance', icon: <ShieldOutlinedIcon sx={iconSx(ACCENT.enterprise.main)} /> },
    ],
  },
];

const fadeInUp = {
  '@keyframes fadeInUp': {
    from: { opacity: 0, transform: 'translateY(24px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
};

export default function PricingPage() {
  const { getAccessTokenSilently } = useOidcContext();
  const orgInfo = useOrgInfo();
  const isBeheerder = useBeheerder();
  const t = useTranslation();
  const [loading, setLoading] = useState<Tier | null>(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [errorSnack, setErrorSnack] = useState(false);

  const handleRequest = useCallback(async (tier: Tier) => {
    setLoading(tier);
    try {
      const token = await getAccessTokenSilently();
      await api.requestPlatformTier(token, tier);
      setSnackOpen(true);
    } catch {
      setErrorSnack(true);
    } finally {
      setLoading(null);
    }
  }, [getAccessTokenSilently]);

  return (
    <Box sx={fadeInUp}>
      {/* Header */}
      <Box sx={{ mb: 5, textAlign: 'center' }}>
        <Typography
          variant="h4"
          sx={{ mb: 1, fontWeight: 700, letterSpacing: '-0.02em', color: 'text.primary' }}
        >
          {t('pricing.page.title')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem', maxWidth: 480, mx: 'auto' }}>
          {t('pricing.page.subtitle')}
        </Typography>
      </Box>

      {!isBeheerder && (
        <Alert severity="info" sx={{ mb: 4, borderRadius: '10px', maxWidth: 600, mx: 'auto' }}>
          {t('pricing.adminOnly')}
        </Alert>
      )}

      {orgInfo?.pending_platform_request && (
        <Alert severity="warning" sx={{ mb: 4, borderRadius: '10px', maxWidth: 600, mx: 'auto' }}>
          {t('pricing.pendingRequest')}
        </Alert>
      )}

      {/* Cards grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 3,
          alignItems: 'stretch',
          maxWidth: 1100,
          mx: 'auto',
        }}
      >
        {TIERS.map((tier, index) => {
          const isCurrent = orgInfo?.hub_tier === tier.id;
          const accent = ACCENT[tier.id];
          const isEnterprise = tier.id === 'enterprise';

          return (
            <Box
              key={tier.id}
              sx={{
                animation: 'fadeInUp 0.5s ease-out both',
                animationDelay: `${index * 0.1}s`,
              }}
            >
              <Box
                sx={{
                  p: 0,
                  borderRadius: '16px',
                  border: isEnterprise ? `1.5px solid ${accent.main}30` : '1px solid rgba(0,0,0,0.08)',
                  background: isEnterprise
                    ? `linear-gradient(165deg, #FFFDF8 0%, ${accent.light} 100%)`
                    : '#fff',
                  boxShadow: isEnterprise
                    ? `0 4px 20px ${accent.main}12, 0 1px 4px rgba(0,0,0,0.04)`
                    : '0 1px 4px rgba(0,0,0,0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                  height: '100%',
                  '&:hover': {
                    boxShadow: isEnterprise
                      ? `0 12px 40px ${accent.main}18, 0 2px 8px rgba(0,0,0,0.06)`
                      : '0 8px 32px rgba(0,0,0,0.08)',
                    transform: 'translateY(-3px)',
                  },
                }}
              >
                {/* Accent top line */}
                <Box
                  sx={{
                    height: 3,
                    background: isEnterprise
                      ? `linear-gradient(90deg, ${accent.main}CC, ${accent.main}44)`
                      : accent.main,
                    opacity: 0.7,
                  }}
                />

                <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  {/* Hero icon */}
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '14px',
                      bgcolor: accent.light,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                    }}
                  >
                    {tier.heroIcon}
                  </Box>

                  {/* Title + tagline */}
                  <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.25rem',
                          letterSpacing: '-0.01em',
                          color: 'text.primary',
                        }}
                      >
                        {t(tier.labelKey)}
                      </Typography>
                      {isCurrent && (
                        <Chip
                          label={t('pricing.currentPlan.chip')}
                          size="small"
                          sx={{
                            bgcolor: '#edf4ef',
                            color: '#00734D',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            height: 22,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', lineHeight: 1.5 }}>
                      {t(tier.taglineKey)}
                    </Typography>
                  </Box>

                  {/* Divider */}
                  <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.06)', mb: 2.5 }} />

                  {/* Features */}
                  <Box sx={{ flexGrow: 1, mb: 3 }}>
                    {tier.features.map((feature) => (
                      <Box
                        key={feature.labelKey}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '8px',
                            bgcolor: accent.light,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Typography sx={{ fontSize: '0.875rem', color: 'text.primary', lineHeight: 1.4 }}>
                          {t(feature.labelKey)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* CTA button */}
                  <Button
                    variant={isEnterprise ? 'contained' : 'outlined'}
                    fullWidth
                    disabled={
                      !isBeheerder ||
                      isCurrent ||
                      loading !== null ||
                      !!orgInfo?.pending_platform_request
                    }
                    onClick={() => handleRequest(tier.id)}
                    disableElevation
                    sx={
                      isEnterprise
                        ? {
                            bgcolor: accent.main,
                            color: '#fff',
                            fontWeight: 600,
                            py: 1.25,
                            borderRadius: '10px',
                            fontSize: '0.875rem',
                            '&:hover': { bgcolor: '#74590F' },
                          }
                        : {
                            borderColor: `${accent.main}40`,
                            color: accent.main,
                            fontWeight: 600,
                            py: 1.25,
                            borderRadius: '10px',
                            fontSize: '0.875rem',
                            '&:hover': {
                              borderColor: accent.main,
                              bgcolor: `${accent.main}08`,
                            },
                          }
                    }
                  >
                    {isCurrent
                      ? t('pricing.button.current')
                      : loading === tier.id
                        ? t('pricing.button.requesting')
                        : t('pricing.button.request')}
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Snackbar
        open={snackOpen}
        autoHideDuration={5000}
        onClose={() => setSnackOpen(false)}
        message={t('pricing.snackbar.submitted')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Snackbar
        open={errorSnack}
        autoHideDuration={5000}
        onClose={() => setErrorSnack(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorSnack(false)}>
          {t('errors.tryAgain')}
        </Alert>
      </Snackbar>
    </Box>
  );
}
